import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';

import useStore from 'Hooks/useStore';
import useAsync from 'Hooks/useAsync';
import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { fetchUserById } from 'Services/Api/_HumanWritten/UserService';
import { stringNotEmpty } from 'Util/TypeGuards';
import { getMessageByKey } from 'Stores/LanguageStore';
import If from 'Views/Components/If/If';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { TextInput } from 'Views/Components/New/TextInput';
import { WizardStepHeader } from '../Shared/WizardStepHeader';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import { PassengerBookingWizardData } from '../../BookingWizardData/PassengerBookingWizardData';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import DynamicFieldsSection from './DynamicFieldsSection';
import { useSubjectUserDisplayNameId } from './useSubjectUserDisplayNameId';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

/**
 * Walk-on bookings tab: hosts the optional primary-passenger name inputs
 * (driven by the collectPrimaryBookingName app-config) plus any admin-configured
 * dynamic fields targeted at PASSENGER_ONLY. Inserted between Passengers and
 * Add-ons in the walk-on wizard.
 */
const BookingDetailsTab = observer(() => {
	const intl = useIntl();
	const store = useStore();
	const { wizardData, setWizardData, innerWizardErrors, setInnerWizardErrors } = useBookingWizardDataStore();

	// NOTE: every hook must run before the instanceof early-return below — bailing out
	// between hooks changes the hook count across renders and crashes the tab.
	// When passenger info is collected, the primary contact ("name on booking") is captured on a
	// passenger (pre-filled + flagged in PassengersTab) rather than here, so don't show this section.
	const collectPrimary = appConfigStore.primaryBookingNameEnabled && !appConfigStore.passengerInfoEnabled;
	const showFirstName = collectPrimary && appConfigStore.primaryBookingFirstNameEnabled;
	const showLastName = collectPrimary && appConfigStore.primaryBookingLastNameEnabled;

	// Resolve the booking-subject user via wizardData.userId. For a customer
	// booking for themselves this is their own record; for staff booking on behalf
	// this is the selected customer (NOT the logged-in staff member). Falls back
	// to the store's own name when no userId is set yet (e.g. guest flow before
	// account creation).
	const subjectUserId = wizardData.userId;
	const isStaffOnBehalf = store.isStaff && stringNotEmpty(subjectUserId) && subjectUserId !== store.userData?.id;
	const customerRequest = useAsync<UserEntity>(
		() => (isStaffOnBehalf ? fetchUserById(subjectUserId!) : Promise.resolve(null as unknown as UserEntity)),
		[isStaffOnBehalf, subjectUserId],
	);
	const subjectFirstName =
		isStaffOnBehalf && customerRequest.type === 'data'
			? (customerRequest.data?.firstName ?? '')
			: (store.firstName ?? '');
	const subjectLastName =
		isStaffOnBehalf && customerRequest.type === 'data'
			? (customerRequest.data?.lastName ?? '')
			: (store.lastName ?? '');

	// Pre-fill from booking subject on first render if no value present yet.
	// Self-heals historical alterations that didn't capture a primary name —
	// opening + saving sets it from the account name.
	useEffect(() => {
		if (!collectPrimary) return;
		// Wait until the customer fetch resolves before pre-filling for staff
		// flows, otherwise we'd briefly populate with the staff's own name.
		if (isStaffOnBehalf && customerRequest.type !== 'data') return;
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof PassengerBookingWizardData) {
			let changed = false;
			if (
				showFirstName &&
				(!newData.innerWizardData.primaryFirstName || newData.innerWizardData.primaryFirstName.trim() === '')
			) {
				newData.innerWizardData.primaryFirstName = subjectFirstName;
				changed = true;
			}
			if (
				showLastName &&
				(!newData.innerWizardData.primaryLastName || newData.innerWizardData.primaryLastName.trim() === '')
			) {
				newData.innerWizardData.primaryLastName = subjectLastName;
				changed = true;
			}
			if (changed) setWizardData(newData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		collectPrimary,
		showFirstName,
		showLastName,
		subjectFirstName,
		subjectLastName,
		isStaffOnBehalf,
		customerRequest.type,
	]);

	// Booking subject's customer type (UserDisplayNameEntity id). Used to filter
	// dynamic fields that are restricted to specific customer types. For staff
	// booking on behalf, this should reflect the selected customer's user type.
	const subjectUserDisplayNameId = useSubjectUserDisplayNameId();

	if (!(wizardData.innerWizardData instanceof PassengerBookingWizardData)) {
		return <></>;
	}

	const inner = wizardData.innerWizardData;

	// Admin-configurable banner shown at the top of the details step for walk-on bookings. The text
	// is driven by the language/translation system; the banner is hidden whenever the translation is
	// empty. This tab only renders for walk-on bookings (see the instanceof early-return above).
	// Resolved here only to decide whether to render — the text itself goes through FormattedMessage
	// so the translation can emphasise part of the sentence with <strong> tags.
	const detailsTabBannerText = getMessageByKey('passenger-details-tab__banner__text', '') ?? '';

	const updateInner = <K extends keyof PassengerBookingWizardData>(
		field: K,
		value: PassengerBookingWizardData[K],
	) => {
		const newData = new BookingWizardData({ ...wizardData });
		if (newData.innerWizardData instanceof PassengerBookingWizardData) {
			(newData.innerWizardData as any)[field] = value;
		}
		setWizardData(newData);
		// Clear the error for the field the user is editing so messages disappear
		// once they start typing; validation re-runs on next-step click.
		if ((innerWizardErrors as any)?.[field]) {
			const cleared = { ...(innerWizardErrors as any) };
			delete cleared[field];
			setInnerWizardErrors(cleared);
		}
	};

	return (
		<div className="booking-wizard-tab booking-details-tab">
			<WizardStepHeader
				iconName="contacts"
				title={intl.formatMessage({
					id: 'booking-wizard__details__header',
					defaultMessage: 'Details',
				})}
				subtitle={intl.formatMessage({
					id: 'booking-wizard__details__subtitle',
					defaultMessage: 'A few extra details for this booking.',
				})}
				showCloseLink={wizardData.wizardMode === 'ALTERATION'}
				bookingToEditId={wizardData?.bookingToEdit ?? ''}
			/>

			{/* Match the vehicle/freight tabs — the flex gap spaces the section cards
			    (DynamicFieldsSection can emit several). */}
			<div className="flex flex-col gap-3">
				{stringNotEmpty(detailsTabBannerText.trim()) && (
					<MessageBox status="info" slim noBottomMargin>
						{
							<p>
								<FormattedMessage
									id="passenger-details-tab__banner__text"
									defaultMessage=""
									values={{ strong: (chunks: React.ReactNode) => <strong>{chunks}</strong> }}
								/>
							</p>
						}
					</MessageBox>
				)}
				<If condition={collectPrimary}>
					<section className="booking-section-card">
						<header className="booking-section-header">
							<span className="booking-section-heading__icon">
								{/* Avoid name="badge" — it collides with the .badge component class and resizes the icon. */}
								<Icon name="person" useMaterialIcons />
							</span>
							<div className="booking-section-header__text">
								<h4 className="booking-section-title">
									{intl.formatMessage({
										id: 'booking-wizard__booking-details__primary-name__header',
										defaultMessage: 'Name on booking',
									})}
								</h4>
								<p className="booking-section-description">
									{intl.formatMessage({
										id: 'booking-wizard__booking-details__primary-name__description',
										defaultMessage:
											'The name of the person this booking is for, so they can be ' +
											'identified at check-in — handy when ' +
											"you're booking on someone else's behalf.",
									})}
								</p>
							</div>
						</header>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
							<If condition={showFirstName}>
								<TextInput
									label={intl.formatMessage({
										id: 'booking-wizard__primary-name__first-name__label',
										defaultMessage: 'First name',
									})}
									required
									value={inner.primaryFirstName ?? ''}
									error={(innerWizardErrors as any)?.primaryFirstName}
									onChange={e => updateInner('primaryFirstName', e.target.value)}
									data-test-id="booking-primary-first-name"
								/>
							</If>
							<If condition={showLastName}>
								<TextInput
									label={intl.formatMessage({
										id: 'booking-wizard__primary-name__last-name__label',
										defaultMessage: 'Last name',
									})}
									required
									value={inner.primaryLastName ?? ''}
									error={(innerWizardErrors as any)?.primaryLastName}
									onChange={e => updateInner('primaryLastName', e.target.value)}
									data-test-id="booking-primary-last-name"
								/>
							</If>
						</div>
					</section>
				</If>

				<DynamicFieldsSection
					bookingType="PASSENGER_ONLY"
					subjectUserDisplayNameId={subjectUserDisplayNameId}
					values={inner.dynamicFieldValues ?? {}}
					errors={(innerWizardErrors as any)?.dynamicFieldValues as Record<string, string> | undefined}
					onChange={next => updateInner('dynamicFieldValues', next)}
					noteField={{
						enabled: appConfigStore.passengerNoteEnabled,
						required: appConfigStore.passengerNoteRequired,
						label:
							getMessageByKey(
								'passenger-booking__additional-information__label',
								'Additional information',
							) ?? 'Additional information',
						placeholder:
							getMessageByKey(
								'passenger-booking__additional-information__placeholder',
								'Enter additional information',
							) ?? undefined,
						value: inner.note ?? '',
						error: (innerWizardErrors as any)?.note,
						onChange: next => updateInner('note', next),
					}}
				/>
			</div>
		</div>
	);
});

export default BookingDetailsTab;
